import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { Clock, Activity, ShieldCheck, Target, Globe, HelpCircle, AlertTriangle, Info, MapPin, Zap, Radio, Mic, LogOut, ChevronLeft } from 'lucide-react';
import clsx from 'clsx';
import { Tooltip } from './Tooltip';

export const GameHeader: React.FC = () => {
  const { turn, maxTurns, ap, maxAp, sectors, timeLeft, currentScenario, reset } = useGameStore();
  const [showLegend, setShowLegend] = useState(false);
  
  const blueControl = sectors.filter(s => s.owner === 'Blue').length;
  const redControl = sectors.filter(s => s.owner === 'Red').length;
  const neutral = sectors.length - blueControl - redControl;
  
  const bluePercent = (blueControl / sectors.length) * 100;
  const redPercent = (redControl / sectors.length) * 100;
  const neutralPercent = (neutral / sectors.length) * 100;

  return (
    <header className="fixed top-4 left-4 right-4 z-50 pointer-events-none animate-slide-in">
      <div className="max-w-7xl mx-auto pointer-events-auto flex flex-col gap-2">
        
        {/* Main Header Bar */}
        <div className="glass-panel rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-6 group relative overflow-hidden">
          
          {/* Subtle Scanline Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] animate-[shimmer_3s_infinite]"></div>

          <div className="flex items-center gap-6">
            <Tooltip content="Return to Lobby">
              <button 
                onClick={reset}
                className="p-2.5 rounded-xl bg-slate-800/50 hover:bg-red-900/20 border border-slate-700 hover:border-red-500/50 text-slate-400 hover:text-red-400 transition-all group/exit flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-bold text-sm tracking-wide hidden md:block">LOBBY</span>
              </button>
            </Tooltip>

            {/* Brand Section & Legend Toggle */}
            <div className="flex items-center gap-4 group/brand cursor-pointer relative z-10" onClick={() => setShowLegend(!showLegend)}>
              <div className="bg-gradient-to-br from-ops-blue/20 to-ops-purple/20 p-2.5 rounded-xl border border-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)] transition-transform duration-500 group-hover/brand:rotate-12">
                <Globe className="w-6 h-6 text-ops-blue animate-pulse-glow-blue" />
              </div>
              <div className="flex flex-col">
                <h1 className="text-lg font-bold text-white tracking-widest leading-none group-hover/brand:text-ops-blue transition-colors font-mono text-glow-blue">
                  NARRATIVE<span className="text-ops-blue group-hover/brand:text-white transition-colors">DOMINANCE</span>
                </h1>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 font-mono tracking-widest uppercase group-hover/brand:text-slate-300 transition-colors">Cognitive Warfare Sim</span>
                  <HelpCircle className="w-3 h-3 text-slate-500 group-hover/brand:text-ops-blue" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Dashboard */}
          <div className="flex items-center gap-2 md:gap-6 bg-slate-950/50 p-2 rounded-xl border border-slate-800 shadow-inner hover:bg-slate-950/70 transition-colors relative z-10">
            <Tooltip content={`Turn ${turn} of ${maxTurns}`}>
              <div className="flex flex-col items-center px-4 border-r border-slate-800 last:border-0 hover:bg-white/5 rounded transition-colors cursor-help py-1">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Turn</span>
                <div className="flex items-center gap-1.5 text-slate-200 font-mono font-bold">
                  <Clock className="w-3.5 h-3.5 text-ops-blue" />
                  <span>{turn}<span className="text-slate-600">/</span>{maxTurns}</span>
                </div>
              </div>
            </Tooltip>
            
            <Tooltip content={`Action Points: ${ap} / ${maxAp}`}>
              <div className="flex flex-col items-center px-4 border-r border-slate-800 last:border-0 hover:bg-white/5 rounded transition-colors cursor-help py-1">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Actions</span>
                <div className="flex items-center gap-1.5 text-slate-200 font-mono font-bold">
                  <Activity className={clsx("w-3.5 h-3.5", ap > 0 ? "text-ops-green" : "text-slate-600")} />
                  <span>{ap}<span className="text-slate-600">/</span>{maxAp}</span>
                </div>
              </div>
            </Tooltip>

            <Tooltip content="Time Remaining">
              <div className={clsx(
                "flex flex-col items-center px-4 rounded-lg transition-all duration-300 py-1",
                timeLeft < 10 ? "bg-ops-red/10 border border-ops-red/30 shadow-[0_0_10px_rgba(239,68,68,0.2)] animate-pulse" : "hover:bg-white/5"
              )}>
                <span className={clsx(
                  "text-[10px] font-bold uppercase tracking-wider mb-0.5",
                  timeLeft < 10 ? "text-red-400" : "text-slate-500"
                )}>Timer</span>
                <div className={clsx(
                  "flex items-center gap-1.5 font-mono font-bold",
                  timeLeft < 10 ? "text-red-400" : "text-slate-200"
                )}>
                  <Clock className="w-3.5 h-3.5" />
                  <span>{Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</span>
                </div>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </header>
  );
};